import {
    addMinutes,
    addMonths,
    addYears,
    differenceInDays,
    differenceInHours,
    differenceInMinutes,
    differenceInMonths,
    differenceInWeeks,
    differenceInYears,
    endOfDay,
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    endOfYear,
    format,
    formatISO,
    isSameDay,
    isSameYear,
    isValid,
    parseISO,
    startOfDay,
    startOfMonth,
    startOfQuarter,
    startOfWeek,
    startOfYear,
    subDays,
    subMinutes,
    subMonths,
    subQuarters,
    subYears,
} from "date-fns";

import { formatInTimeZone, toZonedTime } from "date-fns-tz";

import { keyBy } from "@common/helpers/collections";
import {
    fullDateFormatWithTime,
    fullDateFullYearFormat,
    fullDateLongFormat,
    PST_HOURS_OFFSET,
    toUTCDateOnly,
} from "@common/helpers/date";
import { formatExactDateRangePeriod } from "./formatExactDateRangePeriod";

export interface DateRangePreset {
    id: string;
    label?: string;
    labelFn?: (today: Date) => string;
    getStart: (today: Date) => Date;
    getEnd: (today: Date) => Date;
    followedBySeparator?: boolean;
}

// Order = match precedence: on (start, end) collisions, `find` returns the
// earliest entry. Display order is separate (see `VISIBLE_PRESETS`).
export const DATE_RANGE_PRESETS: DateRangePreset[] = [
    {
        id: "this_week",
        label: "This week",
        getStart: (today) => startOfWeek(today, { weekStartsOn: 0 }),
        getEnd: (today) => endOfDay(endOfWeek(today, { weekStartsOn: 0 })),
    },
    {
        id: "this_month",
        label: "This month",
        getStart: (today) => startOfMonth(today),
        getEnd: (today) => endOfDay(endOfMonth(today)),
    },
    {
        id: "last_month",
        label: "Last month",
        getStart: (today) => startOfMonth(subMonths(today, 1)),
        getEnd: (today) => endOfDay(endOfMonth(subMonths(today, 1))),
    },
    {
        id: "this_quarter",
        label: "This quarter",
        getStart: (today) => startOfQuarter(today),
        getEnd: (today) => endOfDay(endOfQuarter(today)),
    },
    {
        id: "last_quarter",
        label: "Last quarter",
        getStart: (today) => startOfQuarter(subQuarters(today, 1)),
        getEnd: (today) => endOfDay(endOfQuarter(subQuarters(today, 1))),
    },
    {
        id: "this_year",
        label: "This year",
        getStart: (today) => startOfYear(today),
        getEnd: (today) => endOfDay(endOfYear(today)),
    },
    {
        id: "last_year",
        label: "Last year",
        getStart: (today) => startOfYear(subYears(today, 1)),
        getEnd: (today) => endOfDay(endOfYear(subYears(today, 1))),
    },
    {
        id: "this_month_to_date",
        label: "This month to date",
        getStart: (today) => startOfMonth(today),
        getEnd: (today) => endOfDay(today),
    },
    {
        id: "last_month_to_date",
        label: "Last month to date",
        getStart: (today) => startOfMonth(subMonths(today, 1)),
        getEnd: (today) => endOfDay(subMonths(today, 1)),
    },
    {
        id: "this_quarter_to_date",
        label: "This quarter to date",
        getStart: (today) => startOfQuarter(today),
        getEnd: (today) => endOfDay(today),
    },
    {
        id: "last_quarter_to_date",
        label: "Last quarter to date",
        getStart: (today) => startOfQuarter(subQuarters(today, 1)),
        getEnd: (today) => endOfDay(subQuarters(today, 1)),
    },
    {
        id: "this_year_to_date",
        label: "This year to date",
        getStart: (today) => startOfYear(today),
        getEnd: (today) => endOfDay(today),
    },
    {
        id: "last_year_to_date",
        label: "Last year to date",
        getStart: (today) => startOfYear(subYears(today, 1)),
        getEnd: (today) => endOfDay(subYears(today, 1)),
    },
    {
        id: "last_12_months",
        label: "Last 12 months",
        getStart: (today) => startOfMonth(subMonths(today, 12)),
        getEnd: (today) => endOfDay(today),
    },
    {
        id: "last_30_days",
        label: "Last 30 days",
        getStart: (today) => startOfDay(subDays(today, 29)),
        getEnd: (today) => endOfDay(today),
    },
    {
        id: "last_60_days",
        label: "Last 60 days",
        getStart: (today) => startOfDay(subDays(today, 59)),
        getEnd: (today) => endOfDay(today),
    },
    {
        id: "last_90_days",
        label: "Last 90 days",
        getStart: (today) => startOfDay(subDays(today, 89)),
        getEnd: (today) => endOfDay(today),
    },
    {
        id: "this_year_to_last_month",
        label: "This year to last month",
        // January: end < start → empty report (matches QBO).
        getStart: (today) => startOfYear(today),
        getEnd: (today) => endOfDay(endOfMonth(subMonths(today, 1))),
    },
    {
        id: "last_month_to_today",
        label: "Last month to today",
        getStart: (today) => startOfMonth(subMonths(today, 1)),
        getEnd: (today) => endOfDay(today),
    },
    {
        id: "last_quarter_to_today",
        label: "Last quarter to today",
        getStart: (today) => startOfQuarter(subQuarters(today, 1)),
        getEnd: (today) => endOfDay(today),
    },
    {
        id: "last_year_to_today",
        label: "Last year to today",
        getStart: (today) => startOfYear(subYears(today, 1)),
        getEnd: (today) => endOfDay(today),
    },
    {
        id: "next_12_months",
        label: "Next 12 months",
        getStart: (today) => startOfMonth(addMonths(today, 1)),
        getEnd: (today) => endOfDay(endOfMonth(addMonths(today, 12))),
    },
    {
        id: "through_end_of_next_year",
        label: "Through end of next year",
        getStart: (today) => startOfYear(today),
        getEnd: (today) => endOfDay(endOfYear(addYears(today, 1))),
    },
];

export const VISIBLE_PRESETS = [
    "this_month",
    "last_month",
    "this_month_to_date",
    "last_month_to_date",
    "last_month_to_today",
    "this_quarter",
    "last_quarter",
    "this_quarter_to_date",
    "last_quarter_to_date",
    "last_quarter_to_today",
    "this_year",
    "last_year",
    "this_year_to_date",
    "this_year_to_last_month",
    "last_year_to_date",
    "last_year_to_today",
    "last_12_months",
    "last_30_days",
    "last_60_days",
    "last_90_days",
];

// Same as VISIBLE_PRESETS but with "This week" added — used for date filters
// that commonly reference a near-term (often future) window, such as the
// Work page's work start/end date filters.
export const WORK_DATE_VISIBLE_PRESETS = ["this_week", ...VISIBLE_PRESETS];

export const SUGGESTED_PRESETS = {
    accounting: ["last_year", "this_year_to_date"],
    invoicing: ["last_30_days", "last_90_days"],
} as const;

export const DATE_RANGE_PRESETS_BY_ID: Record<string, DateRangePreset> = keyBy(
    DATE_RANGE_PRESETS,
    (p) => p.id,
);

export const ALL_TIME_PRESET_ID = "all_time";

export function formatExplicitDateRangeLabel(
    start: Date | undefined,
    end: Date | undefined,
): string {
    const today = startOfDay(new Date());

    if (start && end) {
        if (isSameDay(start, end)) {
            return format(start, fullDateLongFormat);
        }
        return (
            formatExactDateRangePeriod(start, end) ??
            `${format(start, fullDateLongFormat)} - ${format(
                end,
                fullDateLongFormat,
            )}`
        );
    }
    if (start) {
        const sameYearFrom = format(start, fullDateFullYearFormat);
        const differentYearFrom = format(start, fullDateFullYearFormat);

        return `from ${
            isSameYear(start, today) ? sameYearFrom : differentYearFrom
        }`;
    }
    if (end) {
        const sameYearTo = format(end, fullDateFullYearFormat);
        const differentYearTo = format(end, fullDateFullYearFormat);

        return `to ${isSameYear(end, today) ? sameYearTo : differentYearTo}`;
    }
    return "";
}

export function getDateRangeLabel(
    start: Date | undefined,
    end: Date | undefined,
): string {
    const today = startOfDay(new Date());

    if (start && end) {
        const matchingPreset = DATE_RANGE_PRESETS.find(
            (p) =>
                isSameDay(p.getStart(today), start) &&
                isSameDay(p.getEnd(today), end),
        );

        if (isSameDay(start, end)) {
            return format(start, fullDateLongFormat);
        }

        if (matchingPreset) {
            if (matchingPreset.labelFn) {
                return matchingPreset.labelFn(today);
            }
            if (matchingPreset.label) {
                return matchingPreset.label;
            }
            return "";
        }
    }

    return formatExplicitDateRangeLabel(start, end);
}

export function addTimeZoneOffset(date: Date): Date {
    return addMinutes(date, date.getTimezoneOffset());
}

export function removeTimeZoneOffset(date: Date): Date {
    return subMinutes(date, date.getTimezoneOffset());
}

interface TimeAgoOptions {
    /** When true, omits the trailing " ago" suffix (e.g. "15h" instead of "15h ago"). */
    hideAgo?: boolean;
}

export function timeAgo(dateInput: Date | string, options?: TimeAgoOptions) {
    const date =
        typeof dateInput === "string" ? parseISO(dateInput) : dateInput;
    const now = new Date();
    const suffix = options?.hideAgo ? "" : " ago";

    const years = differenceInYears(now, date);
    if (years) {
        return `${years}y${suffix}`;
    }
    const months = differenceInMonths(now, date);
    if (months) {
        return `${months}mo${suffix}`;
    }

    const weeks = differenceInWeeks(now, date);
    if (weeks) {
        return `${weeks}w${suffix}`;
    }

    const days = differenceInDays(now, date);
    if (days) {
        return `${days}d${suffix}`;
    }

    const hours = differenceInHours(now, date);
    if (hours) {
        return `${hours}h${suffix}`;
    }

    const minutes = differenceInMinutes(now, date);
    if (minutes) {
        return `${minutes}min${suffix}`;
    }

    return "Just now";
}

export function timeAgoInSentence(date: Date | string) {
    return timeAgo(date).toLowerCase();
}

export function formatDate(date: Date) {
    return formatISO(date, { representation: "date" });
}

/** The viewer's current IANA time zone, e.g. for labeling local times or bucketing days server-side. */
export const getViewerTimeZone = (): string =>
    Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Exact-date tooltip content for a relative/"time ago" timestamp: the UTC
 * instant plus the same instant in the viewer's local timezone (labeled with
 * their IANA zone id), so "3mo ago" can be disambiguated without guessing.
 * Returns two lines; render with a line break between them.
 */
export function formatExactDateTooltipLines(date: Date | string): {
    utcLine: string;
    localLine: string;
} {
    const parsedDate = typeof date === "string" ? parseISO(date) : date;
    return {
        utcLine: `${formatInTimeZone(parsedDate, "UTC", fullDateFormatWithTime)} UTC`,
        localLine: `${format(parsedDate, fullDateFormatWithTime)} (${getViewerTimeZone()})`,
    };
}

export function formatIsoDateForDisplay(date: string) {
    return format(parseISO(date), fullDateFullYearFormat);
}

export function formatTokenDate(value: string | Date | null): string {
    if (!value) {
        return "-";
    }
    const date = typeof value === "string" ? parseISO(value) : value;
    if (!isValid(date)) {
        return "-";
    }
    return format(date, fullDateFullYearFormat);
}

export function formatDateForTransaction(date: Date) {
    if (!isValid(date)) {
        return "-";
    }
    return isSameYear(date, new Date())
        ? format(date, "MMM d")
        : format(date, fullDateFullYearFormat);
}

// Transaction and journal entry dates are stored as "PST midnight encoded as
// UTC" (a fixed +8h offset, matching the reports' fixed `date - interval
// '8 hours'` bucketing). Decode with the same fixed offset so the displayed
// calendar day matches the report period. A named zone ("PST") is avoided here
// because it is DST-aware (-7h in summer) and would drift from the reports.
const FIXED_PST_OFFSET = `-0${PST_HOURS_OFFSET}:00`;

export function toBusinessDate(date: Date): Date {
    return toZonedTime(date, FIXED_PST_OFFSET);
}

/** The viewer's calendar day in the browser's timezone (`YYYY-MM-DD`). */
export function getLocalTodayDateOnly(): string {
    return formatDate(new Date());
}

export function toDisplayDateOnly(
    value: string | Date | null | undefined,
): string | null {
    if (typeof value === "string") {
        return value;
    }
    if (!value) {
        return null;
    }
    return toUTCDateOnly(value);
}

export function formatDateOnly(
    dateOnly: string | null | undefined,
    formatString?: string,
): string {
    if (!dateOnly) {
        return "-";
    }
    const date = parseISO(dateOnly);
    if (!isValid(date)) {
        return "-";
    }
    if (formatString) {
        return format(date, formatString);
    }
    return isSameYear(date, new Date())
        ? format(date, "MMM d")
        : format(date, fullDateFullYearFormat);
}

export function formatBusinessDate(date: Date, formatString?: string) {
    if (!isValid(date)) {
        return "-";
    }
    const businessDate = toBusinessDate(date);
    if (formatString) {
        return format(businessDate, formatString);
    }
    return isSameYear(businessDate, toBusinessDate(new Date()))
        ? format(businessDate, "MMM d")
        : format(businessDate, fullDateFullYearFormat);
}
